/**
 * One continuous range calendar: the first day and the last are picked in the
 * same grid, months scroll through it, and the span between them is painted —
 * replacing the two disconnected native date pickers on the new-trip form.
 *
 * Owns only its own pixels: the chosen range is reported through onChange and
 * mirrored into whatever hidden inputs the host form reads, so everything
 * downstream (anchors, submit) keeps consuming #nt-start/#nt-end unchanged.
 */

const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
const MON = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

/** UTC-noon epoch for a year/month/day — immune to DST edges and timezones. */
const at = (y: number, m: number, d: number) => Date.UTC(y, m, d, 12);
const isoOf = (t: number) => new Date(t).toISOString().slice(0, 10);

export interface RangeCalHandle {
  start: string | null;
  end: string | null;
}

export function mountRangeCal(
  host: HTMLElement,
  onChange: (start: string | null, end: string | null) => void
): RangeCalHandle {
  const today = new Date();
  let viewY = today.getFullYear();
  let viewM = today.getMonth();
  const todayIso = isoOf(at(viewY, viewM, today.getDate()));
  const state: RangeCalHandle = { start: null, end: null };

  function monthHTML(y: number, m: number): string {
    const first = new Date(at(y, m, 1));
    const lead = first.getUTCDay(); // blanks before day 1
    const days = new Date(at(y, m + 1, 0)).getUTCDate();
    const cells: string[] = DOW.map((d) => `<span class="rc-dow">${d}</span>`);
    for (let i = 0; i < lead; i++) cells.push(`<span class="rc-blank"></span>`);
    for (let d = 1; d <= days; d++) {
      const iso = isoOf(at(y, m, d));
      const cls = ["rc-day"];
      if (iso === todayIso) cls.push("rc-today");
      if (iso === state.start) cls.push("sel", "sel-start");
      if (iso === state.end) cls.push("sel", "sel-end");
      if (state.start && state.end && iso > state.start && iso < state.end) cls.push("in-range");
      cells.push(
        `<button type="button" class="${cls.join(" ")}" data-iso="${iso}" aria-label="${MON[m]} ${d}, ${y}">${d}</button>`
      );
    }
    return `<div class="rc-month">
      <div class="rc-mname">${MON[m]} ${y}</div>
      <div class="rc-grid">${cells.join("")}</div>
    </div>`;
  }

  function render() {
    const nextM = (viewM + 1) % 12;
    const nextY = viewM === 11 ? viewY + 1 : viewY;
    // jump straight to a month/year instead of paging one month at a time —
    // trips are usually planned months out
    const yearNow = new Date().getFullYear();
    const years = Array.from({ length: 6 }, (_, i) => yearNow + i - 1);
    if (!years.includes(viewY)) years.push(viewY), years.sort();
    host.innerHTML = `
      <div class="rc-head">
        <button type="button" class="rc-nav" data-rc="prev" aria-label="Earlier month">‹</button>
        <span class="rc-jump">
          <select class="rc-sel" data-rc="month" aria-label="Month">
            ${MON.map((n, i) => `<option value="${i}"${i === viewM ? " selected" : ""}>${n}</option>`).join("")}
          </select>
          <select class="rc-sel" data-rc="year" aria-label="Year">
            ${years.map((y) => `<option value="${y}"${y === viewY ? " selected" : ""}>${y}</option>`).join("")}
          </select>
        </span>
        <button type="button" class="rc-nav" data-rc="next" aria-label="Later month">›</button>
      </div>
      <div class="rc-title">Pick the first day, then the last</div>
      <div class="rc-months">${monthHTML(viewY, viewM)}${monthHTML(nextY, nextM)}</div>`;
  }

  host.addEventListener("change", (e) => {
    const el = e.target as HTMLSelectElement;
    if (el.dataset.rc === "month") {
      viewM = Number(el.value);
      render();
    } else if (el.dataset.rc === "year") {
      viewY = Number(el.value);
      render();
    }
  });

  host.addEventListener("click", (e) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-rc], .rc-day");
    if (!el) return;
    if (el.dataset.rc === "prev") {
      viewM--;
      if (viewM < 0) (viewM = 11), viewY--;
      render();
      return;
    }
    if (el.dataset.rc === "next") {
      viewM++;
      if (viewM > 11) (viewM = 0), viewY++;
      render();
      return;
    }
    const iso = el.dataset.iso;
    if (!iso) return;
    if (!state.start || (state.start && state.end)) {
      // a fresh pick, or a third click: start the range over
      state.start = iso;
      state.end = null;
    } else if (iso < state.start) {
      // picked an earlier day while waiting for the end — treat as a new start
      state.start = iso;
    } else {
      state.end = iso;
    }
    render();
    onChange(state.start, state.end);
  });

  render();
  return state;
}
